/* eslint-disable react-hooks/rules-of-hooks */
import Link from "next/link";
import Image from "next/image";
import { format } from "date-fns";
import { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { useQuery } from "react-query";
import { Widget } from "@uploadcare/react-widget";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { omit } from "lodash";
import toast from "react-hot-toast";
// Components
import HeaderNav from "@/kyna/includes/HeaderNav";
import Footer from "@/kyna/includes/Footer";
import InputForm from "@/kyna/forms/InputForm";
import KNDatePicker from "@/kyna/ui/datepicker";
import Spinner from "@/kyna/ui/preloader/Spinner";
import KNModal from "@/kyna/ui/modal/KNModal";
import BaseTemplate from "@/kyna/layouts/BaseTemplate";
import SelectInputForm from "@/kyna/forms/SelectInputForm";
import InputFormV2 from "@/kyna/forms/InputFormV2";
import KNToaster from "@/kyna/ui/toaster/KNToaster";
//helpers
import { MembershipSchema } from "@/kyna/lib/validations/MembershipSchema";
import { KTGet, KTPost } from "@/kyna/lib/db";
import { KNThemeError, KNThemeSuccess } from "@/kyna/ui/toaster/KNTheme";
//constant
import { MEMBERSHIP_TYPE, HONORIFICS } from "@/kyna/lib/StandardReferences";

import MetroBankLogo from "../public/assets/img/metrobank-logo.png";
import GCashLogo from "../public/assets/img/gcash-logo.png";
import GCashEARD from "../public/assets/img/EARD-GCash-QR-Code.png";

// PARTNER LOGOS
// SCHOOLS
import AimLogo from "../public/assets/img/partner-logos/aim-logo.png";
import NULogo from "../public/assets/img/partner-logos/national-university-logo.png";
import USTRedCrossLogo from "../public/assets/img/partner-logos/ust-red-cross-logo.png";
import XavierULogo from "../public/assets/img/partner-logos/xavier-u-logo.png";

// WELFARE
import ChildrenIntLogo from "../public/assets/img/partner-logos/children-international-logo.png";
import FreseniusLogo from "../public/assets/img/partner-logos/fresenius-medical-care-logo.png";
import CouncilWelfareLogo from "../public/assets/img/partner-logos/council-welfare-children-logo.png";

// COMPANIES
import CargillLogo from "../public/assets/img/partner-logos/cargill-logo.png";
import JohnsonLogo from "../public/assets/img/partner-logos/johnson-and-johnson-logo.png";
import ObenGroupLogo from "../public/assets/img/partner-logos/oben-group-logo.png";
import PBBLogo from "../public/assets/img/partner-logos/pbb-logo.png";
import SunlifeLogo from "../public/assets/img/partner-logos/sunlife-logo.png";
import TaguigCityLogo from "../public/assets/img/partner-logos/taguig-city-health-office-logo.png";
import TelusLogo from "../public/assets/img/partner-logos/telus-logo.png";
import ToshibaLogo from "../public/assets/img/partner-logos/toshiba-logo.png";
import UnilabLogo from "../public/assets/img/partner-logos/unilab-logo.png";

const Membership = () => {
  const NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY =
    process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY;
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [iconType, setIconType] = useState("error");
  const [modalTitle, setModalTitle] = useState("Failed");
  const [error, setError] = useState("");
  const [modalMessage, setModalMessage] = useState("Server Failed");
  const contributionRef = useRef();

  const sanitizeHonorifics = (honorifics) => {
    if (honorifics[honorifics?.length - 1] == ".") {
      return honorifics;
    } else {
      return honorifics + ".";
    }
  };

  const formik = useFormik({
    initialValues: {
      first_time_applicant: "",
      membership_type: "",
      honorifics: "",
      others_honorifics: "",
      isVolunteer: false,
      last_name: "",
      first_name: "",
      middle_name: "",
      sex: "",
      birthdate: "",
      contact_no: "",
      email: "",
      facebook: "",
      profession: "",
      school_office: "",
      school_office_address: "",
      block_no: "",
      street_name: "",
      subdivision: "",
      province: "",
      city: "",
      barangay: "",
      payment_method: "",
      proof_of_payment: "",
      contribution: "",
      acknowledgement: "",
      // for organization type of membership
      name_of_org: "",
      socials: "",
      point_person: "",
      // for life members
      signature: "",
      life_membership_delivery: "",
      delivery_address: "",
      pickup_date_time: null,
      know_about_pmha: [],
    },
    validationSchema: MembershipSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      values.honorifics = values?.honorifics
        ? sanitizeHonorifics(
            values.honorifics === "Others"
              ? values.others_honorifics
              : values.honorifics,
          )
        : null;
      const MembershipResponse = await KTPost(
        "/api/membership",
        omit(
          {
            ...values,
            know_about_pmha: values.know_about_pmha.join(", "),
          },
          values,
          ["signature", "proof_of_payment", "others_honorifics"],
        ),
      );
      if (
        MembershipResponse?.data?.err ||
        MembershipResponse?.data?.success === false
      ) {
        const msg =
          MembershipResponse?.data?.err ||
          MembershipResponse?.data?.error_message ||
          "Submission failed. Please review the form for missing fields.";
        toast.error(`Error, ${msg}`, KNThemeError);
        setError(msg);
        if (MembershipResponse?.data?.schemaErrors) {
          formik.setErrors(MembershipResponse.data.schemaErrors);
        }
        setIsLoading(false);
        return;
      }
      if (MembershipResponse?.data?.success) {
        const mediaData = {
          path: values?.proof_of_payment.cdnUrl,
          file_name: values?.proof_of_payment.name,
          orig_name: values?.proof_of_payment.name,
          attach_id: MembershipResponse?.data?.membership?.id,
          attachment_for: "Membership - Proof of Payment",
          user_id: 0,
          membership_id: MembershipResponse?.data?.membership?.id,
          is_image: values?.proof_of_payment.isImage,
          reference_id: values?.proof_of_payment.uuid,
        };
        if (values.membership_type === "Life") {
          const signatureMediaData = {
            path: values?.signature.cdnUrl,
            file_name: values?.signature.name,
            orig_name: values?.signature.name,
            attach_id: MembershipResponse?.data?.membership?.id,
            attachment_for: "Membership - Signature",
            user_id: 0,
            membership_id: MembershipResponse?.data?.membership?.id,
            is_image: values?.signature.isImage,
            reference_id: values?.signature.uuid,
          };

          const signatureResponse = await KTPost(
            "/api/media/applicant",
            signatureMediaData,
          );
          if (!signatureResponse?.data?.success) {
            setIconType("error");
            setIsOpenModal(true);
            setModalTitle("Failed");
            setModalMessage(
              "Server Failed, Cannot upload media files - Signature Image",
            );
            setIsLoading(false);
            setIsOpenModal(true);
            return;
          }
        }
        if (values.proof_of_payment) {
          const uploadProofOfPayment = await KTPost(
            "/api/media/applicant",
            mediaData,
          );

          if (uploadProofOfPayment?.data?.success) {
            const date_today = format(new Date(), "MM/dd/yyyy");
            const time_today = new Date().toLocaleTimeString();
            const res = await fetch("/api/sendgrid", {
              body: JSON.stringify({
                email: values.email,
                // from: "eard@pmha.org.ph",
                from: "membership@pmha.org.ph",
                subject: `Membership - ${
                  values?.membership_type == "Sustaining" ||
                  values?.membership_type == "Corporate"
                    ? `${values.name_of_org}`
                    : `${values.honorifics} ${values.first_name} ${values.last_name}`
                } (${date_today}-${time_today})`,
                // message: message.props.initialValue[0].children[0].text,
                html: `
                <!doctype html>
                  <html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">

                  <head>
                    <title>Membership</title>
                    <meta http-equiv="X-UA-Compatible" content="IE=edge">
                    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1">
                    <style type="text/css">
                      #outlook a {
                        padding: 0;
                      }
                      body {
                        margin: 0;
                        padding: 0;
                        -webkit-text-size-adjust: 100%;
                        -ms-text-size-adjust: 100%;
                      }
                      table,
                      td {
                        border-collapse: collapse;
                        mso-table-lspace: 0pt;
                        mso-table-rspace: 0pt;
                      }
                      img {
                        border: 0;
                        height: auto;
                        line-height: 100%;
                        outline: none;
                        text-decoration: none;
                        -ms-interpolation-mode: bicubic;
                      }
                      p {
                        display: block;
                        margin: 13px 0;
                      }
                    </style>

                    <link rel="preconnect" href="https://fonts.googleapis.com">
                    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">

                    <style type="text/css">
                      @import url(https://fonts.googleapis.com/css?family=Roboto:400,700);
                    </style>

                    <style type="text/css">
                      @media only screen and (min-width:480px) {
                        .mj-column-per-100 {
                          width: 100% !important;
                          max-width: 100%;
                        }
                        .mj-column-per-35 {
                          width: 35% !important;
                          max-width: 35%;
                        }
                        .mj-column-per-65 {
                          width: 65% !important;
                          max-width: 65%;
                        }
                      }
                    </style>
                    <style media="screen and (min-width:480px)">
                      .moz-text-html .mj-column-per-100 {
                        width: 100% !important;
                        max-width: 100%;
                      }
                      .moz-text-html .mj-column-per-35 {
                        width: 35% !important;
                        max-width: 35%;
                      }
                      .moz-text-html .mj-column-per-65 {
                        width: 65% !important;
                        max-width: 65%;
                      }
                    </style>
                    <style type="text/css">
                      @media only screen and (max-width:480px) {
                        table.mj-full-width-mobile {
                          width: 100% !important;
                        }
                        td.mj-full-width-mobile {
                          width: auto !important;
                        }

                        .site-logo,
                        .site-name {
                          display: block;
                          width: 100%;
                        }
                        .site-logo {
                          padding-bottom: 15px;
                        }
                        .site-name > div {
                          padding-left: 0 !important;
                        }

                      }
                    </style>
                  </head>

                  <body style="word-spacing: normal; background-color: #f1f1f1;">
                    <div style="background-color: #f1f1f1; padding: 20px;">
                      <div style="margin: 30px auto; background-color: #ffffff; max-width: 600px;">
                        <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%; background: #193e8c;">
                          <tbody>
                            <tr>
                              <td style="direction: ltr; text-align: center;">
                                <div class="mj-column-per-100 mj-outlook-group-fix" style="text-align: left; direction: ltr; display: inline-block; vertical-align: middle; width:100%;">
                                  <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align: middle;" width="100%">
                                    <tbody>
                                      <tr>
                                        <td align="" style="padding: 20px 25px; word-break: break-word;">
                                          <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse; border-spacing: 0px; margin: auto;">
                                            <tbody>
                                              <tr>
                                                <td class="site-logo" style="">
                                                  <img alt="PMHA" height="auto" src="https://ucarecdn.com/b68fe996-fcc1-479c-ad51-778f8f283205/" style="border: 0; display: block; outline: none; text-decoration: none; height: auto; width: 100%; max-width: 80px; margin: auto;" />
                                                </td>
                                                <td class="site-name" style="">
                                                  <div style="font-family: 'Roboto', sans-serif; font-size: 20px; font-weight: 700; line-height: 28px; text-align: center; text-transform: uppercase; letter-spacing: 2px; color: #ffffff; padding-left: 20px; max-width: 300px; margin: auto;">Philippine Mental Health Association Inc.</div>
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;">
                          <tbody>
                            <tr>
                              <td style="direction: ltr; padding: 0; text-align: center;">
                                <div class="mj-column-per-100 mj-outlook-group-fix" style="text-align: left; direction: ltr; display: inline-block; vertical-align: middle; width: 100%;">
                                  <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align: middle;" width="100%">
                                    <tbody>
                                      <tr>
                                        <td align="center" style="padding: 30px 25px 0; word-break: break-word;">
                                          <div style="font-family: 'Roboto', sans-serif; font-size: 20px; font-weight: 700; line-height: 28px; text-align: center; text-transform: uppercase; letter-spacing: 2px; color: #193e8c;">Membership</div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                  <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width:100%; max-width: 550px;">
                                    <tbody>
                                      <tr>
                                        <td style="direction:ltr; font-size: 0px; padding: 30px 20px 20px;text-align:center;">
                                          <div class="mj-column-per-100 mj-outlook-group-fix" style="font-size:0px;text-align:left;direction:ltr;display:inline-block;vertical-align:middle;width:100%;">
                                            <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align:middle;" width="100%">
                                              <tbody>
                                                <tr>
                                                  <td align="center" style="font-size: 0px; padding: 10px 0; word-break: break-word;">
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 16px; font-weight: 400; line-height: 1; text-align: left; color: #000000;">Hi, <strong>
                                                    ${
                                                      values?.membership_type ==
                                                        "Sustaining" ||
                                                      values?.membership_type ==
                                                        "Corporate"
                                                        ? `${values.name_of_org}`
                                                        : `${values.honorifics} ${values.first_name}`
                                                    }!</strong></div>
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 10px;">We are grateful for your interest in our organization.</div>
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 10px;">Your membership application has been received and is now for review. We will get back to you once we verify your information. Kindly wait for the confirmation of your membership to be sent in your email.</div>
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 10px;">Thank you for choosing PMHA and we appreciate your support for mental health advocacy!</div>
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 10px;">Follow our social media accounts to get to know more about us and our activities.</div>
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <td align="center" style="font-size: 0px; padding: 10px 0 60px; word-break: break-word;">
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 50px;">Sincerely,</div>
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; font-weight: 600; line-height: 22px; text-align: left; color: #000000;">PMHA Team</div>
                                                  </td>
                                                </tr>


                                                <!-- EMAIL NOTIFICATION FOOTER -->
                                                <tr>
                                                  <td align="center" style="font-size: 0px; padding-top: 15px; word-break: break-word; border-top: 1px solid #eee;">
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 14px; font-weight: 600; line-height: 22px; text-align: left; color: #000000;">
                                                      <ul style="list-style: none; padding-left: 0; text-align: center; margin-top: 0; margin-bottom: 5px;">
                                                        <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                          <a href="https://www.facebook.com/PMHAofficial" target="_blank">
                                                            <img style="width: 14px;" src="https://ucarecdn.com/0869ab43-f54d-4f83-8781-8fed79d7938b/" alt="Facebook">
                                                          </a>
                                                        </li>
                                                        <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                          <a href="https://twitter.com/PMHAofficial" target="_blank">
                                                            <img style="width: 14px;" src="https://ucarecdn.com/f8e6e9fd-7f11-4515-b088-e5567a9915ba/" alt="Twitter">
                                                          </a>
                                                        </li>
                                                        <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                          <a href="https://www.instagram.com/pmhaofficial/" target="_blank">
                                                            <img style="width: 14px;" src="https://ucarecdn.com/6b306f1a-796f-404f-86ba-400a0f766e83/" alt="Instagram">
                                                          </a>
                                                        </li>
                                                        <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                          <a href="https://www.youtube.com/channel/UCvZjwCE8FucFXNrcGjgPz_w" target="_blank">
                                                            <img style="width: 14px;" src="https://ucarecdn.com/a822e322-e351-4d21-8bf7-14ac85a19f28/" alt="Youtube">
                                                          </a>
                                                        </li>
                                                        <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                          <a href="https://www.tiktok.com/@pmhaofficial" target="_blank">
                                                            <img style="width: 14px;" src="https://ucarecdn.com/2ed79507-7d8e-499c-afe6-85b2988aeea9/" alt="TikTok">
                                                          </a>
                                                        </li>
                                                      </ul>
                                                    </div>
                                                    <div style="font-family: 'Roboto', sans-serif; font-size: 12px; font-weight: 400; line-height: 22px; text-align: center; color: #000000; letter-spacing: 2px;">PMHA © ${new Date().getFullYear()}</div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </body>

                  </html>
            `,
              }),
              headers: {
                "Content-Type": "application/json",
              },
              method: "POST",
            });
            const { error } = await res.json();
            if (error) {
              // console.log(error);
              return;
            }
            // ADMIN
            const sendToEmails =
              process.env.NEXT_PUBLIC_KYNA_ENV === "development"
                ? ["kynatech.dev@gmail.com"]
                : process.env.NEXT_PUBLIC_KYNA_ENV === "production"
                  ? ["membership@pmha.org.ph"]
                  : ["judith.kyna@gmail.com"];

            const BaseURL = process.env.NEXT_PUBLIC_VERCEL_URL;
            const adminRes = await fetch("/api/sendgrid", {
              body: JSON.stringify({
                email: sendToEmails,
                // from: "eard@pmha.org.ph",
                subject: `New Membership - ${
                  values?.membership_type == "Sustaining" ||
                  values?.membership_type == "Corporate"
                    ? `${values.name_of_org}`
                    : `${values.honorifics} ${values.first_name} ${values.last_name}`
                } (${date_today}-${time_today})`,
                // message: message.props.initialValue[0].children[0].text,
                html: `
              <!doctype html>
              <html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
              
              <head>
                <title>Membership</title>
                <meta http-equiv="X-UA-Compatible" content="IE=edge">
                <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1">
                <style type="text/css">
                  #outlook a {
                    padding: 0;
                  }
                  body {
                    margin: 0;
                    padding: 0;
                    -webkit-text-size-adjust: 100%;
                    -ms-text-size-adjust: 100%;
                  }
                  table,
                  td {
                    border-collapse: collapse;
                    mso-table-lspace: 0pt;
                    mso-table-rspace: 0pt;
                  }
                  img {
                    border: 0;
                    height: auto;
                    line-height: 100%;
                    outline: none;
                    text-decoration: none;
                    -ms-interpolation-mode: bicubic;
                  }
                  p {
                    display: block;
                    margin: 13px 0;
                  }
                  table.client-details tbody tr td {
                    vertical-align: top;
                    padding-left: 5px;
                    padding-right: 5px;
                  }
                  .tbl-label {
                    width: 35%;
                  }
                  .tbl-value {
                    width: 65%;
                  }
                </style>
              
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
              
                <style type="text/css">
                  @import url(https://fonts.googleapis.com/css?family=Roboto:400,700);
                </style>
              
                <style type="text/css">
                  @media only screen and (min-width:480px) {
                    .mj-column-per-100 {
                      width: 100% !important;
                      max-width: 100%;
                    }
                    .mj-column-per-35 {
                      width: 35% !important;
                      max-width: 35%;
                    }
                    .mj-column-per-65 {
                      width: 65% !important;
                      max-width: 65%;
                    }
                  }
                </style>
                <style media="screen and (min-width:480px)">
                  .moz-text-html .mj-column-per-100 {
                    width: 100% !important;
                    max-width: 100%;
                  }
                  .moz-text-html .mj-column-per-35 {
                    width: 35% !important;
                    max-width: 35%;
                  }
                  .moz-text-html .mj-column-per-65 {
                    width: 65% !important;
                    max-width: 65%;
                  }
                </style>
                <style type="text/css">
                  @media only screen and (max-width:480px) {
                    table.mj-full-width-mobile {
                      width: 100% !important;
                    }
                    td.mj-full-width-mobile {
                      width: auto !important;
                    }
              
                    .site-logo,
                    .site-name {
                      display: block;
                      width: 100%;
                    }
                    .site-logo {
                      padding-bottom: 15px;
                    }
                    .site-name > div {
                      padding-left: 0 !important;
                    }
                    .donation-details {
                      padding: 10px 0 0 !important;
                    }
              
                  }
                </style>
              </head>
              
              <body style="word-spacing: normal; background-color: #f1f1f1;">
                <div style="background-color: #f1f1f1; padding: 20px;">
                  <div style="margin: 30px auto; background-color: #ffffff; max-width: 600px;">
                    <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%; background: #193e8c;">
                      <tbody>
                        <tr>
                          <td style="direction: ltr; text-align: center;">
                            <div class="mj-column-per-100 mj-outlook-group-fix" style="text-align: left; direction: ltr; display: inline-block; vertical-align: middle; width:100%;">
                              <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align: middle;" width="100%">
                                <tbody>
                                  <tr>
                                    <td align="" style="padding: 20px 25px; word-break: break-word;">
                                      <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse; border-spacing: 0px; margin: auto;">
                                        <tbody>
                                          <tr>
                                            <td class="site-logo" style="">
                                            <img alt="PMHA" height="auto" src="https://ucarecdn.com/b68fe996-fcc1-479c-ad51-778f8f283205/" style="border: 0; display: block; outline: none; text-decoration: none; height: auto; width: 100%; max-width: 80px; margin: auto;" />
                                            </td>
                                            <td class="site-name" style="">
                                              <div style="font-family: 'Roboto', sans-serif; font-size: 20px; font-weight: 700; line-height: 28px; text-align: center; text-transform: uppercase; letter-spacing: 2px; color: #ffffff; padding-left: 20px; max-width: 300px; margin: auto;">Philippine Mental Health Association Inc.</div>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;">
                      <tbody>
                        <tr>
                          <td style="direction: ltr; padding: 0; text-align: center;">
                            <div class="mj-column-per-100 mj-outlook-group-fix" style="text-align: left; direction: ltr; display: inline-block; vertical-align: middle; width: 100%;">
                              <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align: middle;" width="100%">
                                <tbody>
                                  <tr>
                                    <td align="center" style="padding: 30px 25px 0; word-break: break-word;">
                                      <div style="font-family: 'Roboto', sans-serif; font-size: 20px; font-weight: 700; line-height: 28px; text-align: center; text-transform: uppercase; letter-spacing: 2px; color: #193e8c;">Membership</div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                              <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width:100%; max-width: 550px;">
                                <tbody>
                                  <tr>
                                    <td style="direction:ltr; font-size: 0px; padding: 30px 20px 20px;text-align:center;">
                                      <div class="mj-column-per-100 mj-outlook-group-fix" style="font-size:0px;text-align:left;direction:ltr;display:inline-block;vertical-align:middle;width:100%;">
                                        <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align:middle;" width="100%">
                                          <tbody>
                                            <tr>
                                              <td align="center" style="font-size: 0px; padding: 10px 0; word-break: break-word;">
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 16px; font-weight: 400; line-height: 1; text-align: left; color: #000000;">Hi, <strong>PMHA Team!</strong></div>
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 10px;">You have received a new membership application.</div>
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: left; color: #000000; padding-top: 10px; margin-bottom: 30px;">
                                                  Here are the member's details:
                                                  <div class="donation-details" style="padding: 10px 15px 0;">
                                                    <table class="client-details" style="width: 100%;">
                                                      <tbody>
                                                        <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Membership Status:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>New</strong></td>
                                                        </tr>
                                                        <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Type of Membership:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${
                                                            values.membership_type
                                                          }</strong></td>
                                                        </tr>
                                                        ${
                                                          values?.point_person
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Name of Point Person</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>
                                                            ${values.honorifics} ${values.point_person}</strong>
                                                          </td>
                                                        </tr>
                                                        `
                                                            : ``
                                                        }
                                                        ${
                                                          values?.first_name
                                                            ? `
                                                            <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Name:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.honorifics} ${values.first_name} ${values.middle_name} ${values.last_name}</strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                        ${
                                                          values?.sex
                                                            ? `<tr>
                                                              <td class="tbl-label" style="font-size: 12px;">Gender:</td>
                                                              <td class="tbl-value" style="font-size: 12px;"><strong>${values.sex}</strong></td>
                                                          </tr>`
                                                            : ``
                                                        }
                                                        ${
                                                          values?.birthdate
                                                            ? `
                                                            <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Birthdate:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>
                                                          ${new Date(
                                                            values.birthdate,
                                                          ).toLocaleDateString()}
                                                          </strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                        <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Contact No:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${
                                                            values.contact_no
                                                          }</strong></td>
                                                        </tr>
                                                        <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Email:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${
                                                            values.email
                                                          }</strong></td>
                                                        </tr>
                                                        ${
                                                          values?.facebook
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Facebook URL:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.facebook}</strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                        ${
                                                          values?.profession
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Profession / Occupation:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.profession}</strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                        ${
                                                          values?.school_office
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">School / Office (if any):</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.school_office}</strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                        ${
                                                          values?.school_office_address
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">School / Office Address (if any):</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.school_office_address}</strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                        
                                                        <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Payment Method:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${
                                                            values.payment_method
                                                          }</strong></td>
                                                        </tr>
                                                        <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Payment:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>Php ${
                                                            values.contribution
                                                          }</strong></td>
                                                        </tr>
                                                        ${
                                                          values?.name_of_org
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Name of School / Agency / Organization / Office:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.name_of_org}</strong></td>
                                                        </tr>
                                                          `
                                                            : ``
                                                        }
                                                        ${
                                                          values?.socials
                                                            ? `
                                                          <tr>
                                                          <td class="tbl-label" style="font-size: 12px;">Social Media Account / Website / Agency / Organization / Office:</td>
                                                          <td class="tbl-value" style="font-size: 12px;"><strong>${values.socials}</strong></td>
                                                        </tr>`
                                                            : ``
                                                        }
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: center; color: #000000;">To check the member's application, please login to your account.</div>
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 14px; line-height: 22px; text-align: center; color: #000000; padding-top: 20px; padding-bottom: 10px;">
                                                  <a href="${
                                                    process.env
                                                      .NEXT_PUBLIC_VERCEL_URL
                                                  }/app/dashboard" style="font-size: 14px; font-weight: 600; text-transform: uppercase; text-decoration: none; letter-spacing: 1px; background-color: #2eafcf; color: #fff; padding: 8px 15px; border-radius: 50px; display: block; max-width: 170px; width: 100%; margin: auto;">Click here to Login</a>
                                                </div>
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 12px; line-height: 20px; text-align: center; color: #000000; padding-bottom: 20px;">If the button above is not working, please <a href="${
                                                  process.env
                                                    .NEXT_PUBLIC_VERCEL_URL
                                                }/app/dashboard" target="_blank" style="text-decoration: underline; color: #2eafcf;">click here</a> to login.</div>
                                              </td>
                                            </tr>
              
              
                                            <!-- EMAIL NOTIFICATION FOOTER -->
                                            <tr>
                                              <td align="center" style="font-size: 0px; padding-top: 15px; word-break: break-word; border-top: 1px solid #eee;">
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 14px; font-weight: 600; line-height: 22px; text-align: left; color: #000000;">
                                                  <ul style="list-style: none; padding-left: 0; text-align: center; margin-top: 0; margin-bottom: 5px;">
                                                    <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                      <a href="https://www.facebook.com/PMHAofficial" target="_blank">
                                                        <img style="width: 14px;" src="https://ucarecdn.com/0869ab43-f54d-4f83-8781-8fed79d7938b/" alt="Facebook">
                                                      </a>
                                                    </li>
                                                    <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                      <a href="https://twitter.com/PMHAofficial" target="_blank">
                                                        <img style="width: 14px;" src="https://ucarecdn.com/f8e6e9fd-7f11-4515-b088-e5567a9915ba/" alt="Twitter">
                                                      </a>
                                                    </li>
                                                    <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                      <a href="https://www.instagram.com/pmhaofficial/" target="_blank">
                                                        <img style="width: 14px;" src="https://ucarecdn.com/6b306f1a-796f-404f-86ba-400a0f766e83/" alt="Instagram">
                                                      </a>
                                                    </li>
                                                    <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                      <a href="https://www.youtube.com/channel/UCvZjwCE8FucFXNrcGjgPz_w" target="_blank">
                                                        <img style="width: 14px;" src="https://ucarecdn.com/a822e322-e351-4d21-8bf7-14ac85a19f28/" alt="Youtube">
                                                      </a>
                                                    </li>
                                                    <li style="display: inline-block; padding: 0 8px; margin-left: 0;">
                                                      <a href="https://www.tiktok.com/@pmhaofficial" target="_blank">
                                                        <img style="width: 14px;" src="https://ucarecdn.com/2ed79507-7d8e-499c-afe6-85b2988aeea9/" alt="TikTok">
                                                      </a>
                                                    </li>
                                                  </ul>
                                                </div>
                                                <div style="font-family: 'Roboto', sans-serif; font-size: 12px; font-weight: 400; line-height: 22px; text-align: center; color: #000000; letter-spacing: 2px;">PMHA © ${new Date().getFullYear()}</div>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </body>
              
              </html>
            `,
              }),
              headers: {
                "Content-Type": "application/json",
              },
              method: "POST",
            });
            const { adminerror } = await adminRes.json();
            if (adminerror) {
              console.log(adminerror);
              return;
            }
            setIconType("info");
            setModalTitle("Success");
            setModalMessage(
              "Thank you for your support for PMHA and the cause of mental health! Your membership application has been sent for approval.",
            );
          } else {
            setIconType("error");
            setModalTitle("Failed");
            setModalMessage(
              "Server Failed, Cannot upload media files. Please try again. - Proof of Payment Image",
            );
          }
        }

        // toast.success("Successfully Sent", {
        //   ...KNThemeSuccess,
        //   position: "top-center",
        // });
      } else {
        setIconType("error");
        setModalTitle("Failed");
        setModalMessage("There's an error occur, please try again.");
      }
      setIsLoading(false);
      setIsOpenModal(true);
    },
  });

  console.log({ ...formik.errors });
  console.log(formik.values.proof_of_payment);
  console.log(formik.values.payment_method);
  const {
    data: ProvinceData,
    status: ProvinceStatus,
    isLoading: ProvinceIsLoading,
  } = useQuery(
    "Provinces",
    async () =>
      await KTGet("/api/maintenance/places/provinces", {
        limit: 100,
        orderBy: "prov_name,asc",
        select: "id,prov_code,prov_name",
      }),
  );
  const {
    data: CitiesData,
    status: CitiesStatus,
    isLoading: CitiesIsLoading,
    isFetching: CitiesIsFetching,
    refetch: CitiesRefetch,
  } = useQuery(
    "Cities",
    async () =>
      formik.values.province &&
      (await KTGet("/api/maintenance/places/cities", {
        limit: 100,
        orderBy: "city_name,asc",
        prov_code: formik.values.province,
        select: "id, city_code, city_name",
      })),
    {
      enabled: false,
    },
  );
  const {
    data: BarangayData,
    status: BarangayStatus,
    isLoading: BarangayIsLoading,
    isFetching: BarangayIsFetching,
    refetch: BarangayRefetch,
  } = useQuery(
    "Barangay",
    async () =>
      formik.values.city &&
      (await KTGet("/api/maintenance/places/brgys", {
        limit: 100,
        orderBy: "brgy_loc,asc",
        city_code: formik.values.city,
        select: "id, brgy_loc",
      })),
    {
      enabled: false,
    },
  );

  useEffect(() => {
    CitiesRefetch();
  }, [formik.values.province]);

  useEffect(() => {
    BarangayRefetch();
  }, [formik.values.city]);

  useEffect(() => {
    MEMBERSHIP_TYPE.map((type) => {
      if (type.name == formik.values.membership_type) {
        contributionRef.current.value = type.price;
        formik.setFieldValue("contribution", type.price);
      }
    });
  }, [formik.values.membership_type]);

  const handleCheckBoxInput = (value, checked) => {
    if (checked) {
      const values = [...formik.values.know_about_pmha, value];
      formik.setFieldValue("know_about_pmha", values);
    } else {
      const values = formik.values.know_about_pmha.filter((i) => i !== value);
      formik.setFieldValue("know_about_pmha", values);
    }
  };

  return (
    <main>
      <HeaderNav />
      <section className="xl:container pt-36 pb-10 px-8 mx-auto">
        <div className="max-w-6xl mx-auto">
          <h1 className="leading-normal text-black text-4xl">Membership</h1>
          <p className="text-xl mb-3">
            PMHA is accepting online applications for membership!
          </p>
          <p className="text-lg">
            Become a PMHA member to be part of our roster of volunteers and
            professional and company partners. The membership fees also help
            sustain the mental health services for indigent service users and in
            the implementation of PMHA's programs and services.
          </p>

          <div className="pt-8">
            <h2 className="text-blue text-2xl">Types of Membership</h2>
            <div className="lg:flex block items-stretch">
              <div className="flex-1 bg-gray-100 sm:p-8 p-6 rounded-lg my-4 lg:mx-4 mx-0">
                <h3 className="text-lg uppercase">Regular</h3>
                <div className="overflow-auto">
                  <table className="w-full">
                    <tbody>
                      <tr>
                        <td className="align-top w-[50%] py-2 pr-3">
                          <span className="font-bold">Contributing</span>
                          <br />
                          <span className="text-sm">(Professional)</span>
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right">
                          P 300
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right text-sm">
                          (for 1 year)
                        </td>
                      </tr>
                      <tr>
                        <td className="align-top w-[50%] py-2 pr-3">
                          <span className="font-bold">Life</span>
                          <br />
                          <span className="text-sm">(Individual)</span>
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right">
                          P 3,000
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right text-sm">
                          (lifetime)
                        </td>
                      </tr>
                      <tr>
                        <td className="align-top w-[50%] py-2 pr-3">
                          <span className="font-bold">Sustaining</span>
                          <br />
                          <span className="text-sm">
                            (Government and Non-Government Organizations,
                            Agencies and Schools)
                          </span>
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right">
                          P 3,000
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right text-sm">
                          (for 1 year)
                        </td>
                      </tr>
                      <tr>
                        <td className="align-top w-[50%] py-2 pr-3">
                          <span className="font-bold">Corporate</span>
                          <br />
                          <span className="text-sm">
                            (Corporation and Big Companies)
                          </span>
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right">
                          P 10,000
                        </td>
                        <td className="align-top w-[25%] py-2 px-3 text-right text-sm">
                          (for 1 year)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {/*  */}
              <div className="flex-1 bg-gray-100 sm:p-8 p-6 rounded-lg my-4 lg:mx-4 mx-0">
                <h3 className="text-lg uppercase">
                  Associate - students (for 1 year)
                </h3>
                <div className="overflow-auto">
                  <table className="w-full">
                    <tbody>
                      <tr>
                        <td className="w-[70%] font-bold py-2 pr-3">
                          Junior High School
                        </td>
                        <td className="w-[30%] py-2 px-3 text-right">P 20</td>
                      </tr>
                      <tr>
                        <td className="w-[70%] font-bold py-2 pr-3">
                          Senior High School
                        </td>
                        <td className="w-[30%] py-2 px-3 text-right">P 50</td>
                      </tr>
                      <tr>
                        <td className="w-[70%] font-bold py-2 pr-3">College</td>
                        <td className="w-[30%] py-2 px-3 text-right">P 100</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-8">
            <h2 className="text-blue text-2xl">Membership Privileges</h2>
            <ul className="list-disc list-inside text-base pl-3 pb-5">
              <li>
                Become part of the mental health promotion and support the cause
                of Mental Health;
              </li>
              <li>
                Help sustain the mental health support we provide for indigent
                service users;
              </li>
              <li>
                Priority Invitation to PMHA events and activities exclusive for
                members
              </li>
              <li>Exclusive discounts to PMHA lecture forum and seminars.</li>
            </ul>
            <div>
              <h3 className="text-lg">
                Additional Privileges for Life Members
              </h3>
              <ul className="list-disc list-inside text-base pl-3 pb-5">
                <li>PMHA Life Membership Card PVC</li>
                <li>PMHA Membership Lapel Pin</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg">
                Additional Privileges for Sustaining Members
              </h3>
              <ul className="list-disc list-inside text-base pl-3 pb-5">
                <li>One webinar at a DISCOUNTED RATE;</li>
                <li>
                  Inclusion of their organization's logo in our PMHA
                  Orientations and Social Media Accounts as our stakeholders
                  (optional).
                </li>
                <li>Maximum of 10 free slots for PMHA Orientation and Tour</li>
                <li>Mental Health Career Talks (Upon request)</li>
                <li>
                  Maximum of 2 discounted slots for paid PMHA Lecture-fora
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg">
                Additional Privileges for Corporate Members
              </h3>
              <ul className="list-disc list-inside text-base pl-3 pb-5">
                <li>One FREE basic Mental Health Webinar;</li>
                <li>
                  Inclusion of their organization's logo in our PMHA
                  Orientations and Social Media Accounts as our stakeholders
                  (optional).
                </li>
                <li>Maximum of 15 free slots for Orientation and Tour</li>
                <li>
                  Maximum of 3 discounted slots for paid PMHA Lecture-fora
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP FORM */}
      <section className="bg-gray-100 py-14 md:px-10 sm:px-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-center">
              Join us and be a mental health advocate!
            </h2>
            <p className="text-lg text-center">
              Fill out the form below and help us in enhancing the well-being of
              all!
            </p>
          </div>
          <div className="bg-white shadow-md rounded-md">
            <div className="bg-[#193E8B] rounded-md py-3 md:px-10 sm:px-8 px-6">
              <h3 className="text-white text-lg uppercase mb-0 tracking-wider">
                Membership form
              </h3>
            </div>
            <div className="py-8 sm:px-8 px-5">
              <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-4 mb-8">
                <div className="col-span-1">
                  <label htmlFor="" className="field-label">
                    Membership Status <span className="text-red-500">*</span>
                  </label>
                  <fieldset
                    id="first_time_applicant"
                    name="first_time_applicant"
                    className="flex items-center gap-5"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        id="new-member"
                        type="radio"
                        name="first_time_applicant"
                        onChange={(e) => {
                          formik.setFieldValue("first_time_applicant", true);
                        }}
                        onBlur={formik.handleBlur}
                        className="w-4 h-4"
                      />
                      <label htmlFor="new-member">New Member</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        id="member-renewal"
                        type="radio"
                        name="first_time_applicant"
                        onChange={(e) => {
                          formik.setFieldValue("first_time_applicant", false);
                        }}
                        onBlur={formik.handleBlur}
                        className="w-4 h-4"
                      />
                      <label htmlFor="member-renewal">Membership Renewal</label>
                    </div>
                  </fieldset>
                  <div className="text-red-400 text-xs help-block">
                    {formik.touched.first_time_applicant &&
                      formik.errors.first_time_applicant && (
                        <div>{formik.errors.first_time_applicant}</div>
                      )}
                  </div>
                </div>
                <div className="col-span-1">
                  <label htmlFor="" className="field-label">
                    Type of Membership <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="membership_type"
                    id="membership_type"
                    className="form-control"
                    onChange={(e) => {
                      formik.setFieldValue("membership_type", e.target.value);
                    }}
                    value={formik.values.membership_type}
                    onBlur={formik.handleBlur}
                  >
                    <option value="" selected>
                      Select Types
                    </option>
                    {MEMBERSHIP_TYPE.map((type, i) => {
                      return (
                        <option key={i} value={type.name}>
                          {type.name}
                        </option>
                      );
                    })}
                  </select>
                  <div className="help-block text-red-400 text-xs">
                    {formik.touched.membership_type &&
                      formik.errors.membership_type && (
                        <div>{formik.errors.membership_type}</div>
                      )}
                  </div>
                </div>
              </div>
              {formik.values.membership_type !== "Sustaining" &&
              formik.values.membership_type !== "Corporate" &&
              formik.values.membership_renewal !== "Sustaining" &&
              formik.values.membership_renewal !== "Corporate" ? (
                <form onSubmit={formik.handleSubmit} className="">
                  {/* PERSONAL INFORMATION */}
                  <div className="border-b border-gray-200 pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Personal Information
                      </h4>
                    </header>
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <SelectInputForm
                          id="honorifics"
                          placeholder="Salutation"
                          label="Salutation *"
                          formik={formik}
                          options={HONORIFICS}
                        />
                      </div>
                      {formik.values.honorifics === "Others" && (
                        <div className="col-span-1">
                          <label htmlFor="" className="field-label">
                            Other Salutation
                          </label>
                          <InputFormV2
                            id="others_honorifics"
                            name="others_honorifics"
                            placeholder="Please specify"
                            formik={formik}
                          />
                        </div>
                      )}
                    </div>
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <InputForm
                          type="text"
                          id="first_name"
                          name="first_name"
                          placeholder="First Name"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Middle Name <span className="text-red-500">*</span>
                        </label>
                        <InputForm
                          type="text"
                          id="middle_name"
                          name="middle_name"
                          placeholder="Middle Name"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <InputForm
                          type="text"
                          id="last_name"
                          name="last_name"
                          placeholder="Last Name"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Gender <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="sex"
                            id="sex"
                            onChange={(e) => {
                              formik.setFieldValue("sex", e.target.value);
                            }}
                            onBlur={formik.handleBlur}
                            className="form-control !mb-0"
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Non-binary">Non-binary</option>
                            <option value="Prefer not to say">
                              Prefer not to say
                            </option>
                          </select>
                          <div className="help-block text-red-400 text-xs">
                            {formik.touched.sex && formik.errors.sex && (
                              <div>{formik.errors.sex}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-grop custom-date-field">
                          <label htmlFor="" className="field-label">
                            Birthdate <span className="text-red-500">*</span>
                          </label>
                          <KNDatePicker
                            setDate={(date) => {
                              formik.setFieldValue("birthdate", date);
                            }}
                            placeholderText={"Birthdate"}
                          />
                          <div className="help-block text-red-400 text-xs">
                            {formik.touched.birthdate &&
                              formik.errors.birthdate && (
                                <div>{formik.errors.birthdate}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Contact No. <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <div className="form-control !w-16 leading-7 tracking-wider">
                              +63
                            </div>
                            <input
                              type="number"
                              id="contact_no"
                              name="contact_no"
                              placeholder="9123456789"
                              className="form-control"
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "contact_no",
                                  e.target.value,
                                );
                              }}
                              value={formik.values?.contact_no}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className="help-block text-red-400 text-xs">
                            {formik.touched.contact_no &&
                              formik.errors.contact_no && (
                                <div>{formik.errors.contact_no}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <InputForm
                          type="text"
                          id="email"
                          name="email"
                          placeholder="Email"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                        {error && (
                          <div className="help-block text-red-400 text-xs">
                            <div>{error}</div>
                          </div>
                        )}
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Facebook Link
                        </label>
                        <InputForm
                          type="text"
                          id="facebook"
                          name="facebook"
                          placeholder="Facebook Link"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full !mb-0"
                        />
                      </div>
                    </div>
                    {/* MEMBERSHIP ID */}
                    {formik.values.membership_type === "Life" ? (
                      <>
                        <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4 mb-2">
                          <div className="col-span-1 sm:mb-0 mb-5">
                            <h5 className="text-gray-700 text-base">
                              Member&apos;s Signature{" "}
                            </h5>
                            <p className="text-blue-300 text-sm mb-5">
                              The signature will be attached in the PVC ID of
                              the Life Member. Please refer to the sample
                              picture.
                            </p>
                            <div className="field-label">
                              Upload Signature{" "}
                              <span className="text-red-500">*</span>
                            </div>
                            <Widget
                              className=""
                              publicKey={NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
                              id="signature"
                              name="signature"
                              tabs="file"
                              onFileSelect={(file) => {
                                console.log("File changed: ", file);
                                if (file) {
                                  file.progress((info) =>
                                    console.log(
                                      "File progress: ",
                                      info.progress,
                                    ),
                                  );
                                  file.done((info) => {
                                    console.log("File uploaded: ", info);
                                    // setCVPic(info.cdnUrl)
                                    formik.setFieldValue("signature", info);
                                    // saveMediaFile(info)
                                  });
                                }
                              }}
                            />
                            <div className="text-red-400 text-xs help-block mt-1">
                              {formik.touched.signature &&
                                formik.errors.signature && (
                                  <div>{formik.errors.signature}</div>
                                )}
                            </div>
                          </div>
                          <div className="col-span-1">
                            <label htmlFor="" className="field-label">
                              Sample Life Member Card
                            </label>
                            <div className="border border-gray-300 p-1">
                              <Image
                                src={"/assets/img/sample-membership-card.png"}
                                alt="sample membership card"
                                width={1011}
                                height={639}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-x-4 mb-2">
                          <div className="col-span-1 mb-3">
                            <label htmlFor="" className="field-label">
                              Life Membership ID Delivery{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <fieldset
                              id="life_membership_delivery"
                              name="life_membership_delivery"
                              className="flex items-center gap-10"
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  id="pickup_from_pmha"
                                  type="radio"
                                  name="life_membership_delivery"
                                  value="Pick-up from PMHA Office"
                                  checked={
                                    formik.values.life_membership_delivery ===
                                    "Pick-up from PMHA Office"
                                  }
                                  onChange={formik.handleChange}
                                  // onChange={(e) => {
                                  //   formik.setFieldValue(
                                  //     "life_membership_delivery",
                                  //     true
                                  //   );
                                  // }}
                                  onBlur={formik.handleBlur}
                                  className="w-4 h-4"
                                />
                                <label htmlFor="pickup_from_pmha">
                                  Pick-up from PMHA Office
                                </label>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  id="delivery_via_lbc"
                                  type="radio"
                                  name="life_membership_delivery"
                                  value="Delivery via LBC"
                                  checked={
                                    formik.values.life_membership_delivery ===
                                    "Delivery via LBC"
                                  }
                                  onChange={formik.handleChange}
                                  // onChange={(e) => {
                                  //   formik.setFieldValue(
                                  //     "life_membership_delivery",
                                  //     false
                                  //   );
                                  // }}
                                  onBlur={formik.handleBlur}
                                  className="w-4 h-4"
                                />
                                <label htmlFor="delivery_via_lbc">
                                  Delivery via LBC
                                </label>
                              </div>
                            </fieldset>
                            <div className="text-red-400 text-xs help-block">
                              {formik.touched.life_membership_delivery &&
                                formik.errors.life_membership_delivery && (
                                  <div>
                                    {formik.errors.life_membership_delivery}
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="col-span-1">
                            {formik.values.life_membership_delivery ===
                            "Delivery via LBC" ? (
                              <>
                                <label htmlFor="" className="field-label">
                                  Delivery Address
                                </label>
                                <input
                                  type="text"
                                  id="delivery_address"
                                  name="delivery_address"
                                  placeholder="Delivery Address"
                                  className="form-control"
                                  onChange={(e) => {
                                    formik.setFieldValue(
                                      "delivery_address",
                                      e.target.value,
                                    );
                                  }}
                                  value={formik.values?.delivery_address}
                                  onBlur={formik.handleBlur}
                                />
                                <p className="text-sm text-blue">
                                  Your membership ID will be delivered to this
                                  address only. Please ensure all details are
                                  accurate and complete.
                                </p>
                              </>
                            ) : null}
                            {formik.values.life_membership_delivery ===
                            "Pick-up from PMHA Office" ? (
                              <>
                                <p className="text-base text-blue font-bold mb-2">
                                  8:00 AM to 3:00 PM, Wednesday to Friday only.
                                </p>
                                <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                                  <div className="col-span-1">
                                    <label htmlFor="" className="field-label">
                                      Date and Time of Pick Up
                                    </label>
                                    <DatePicker
                                      id="pickup_date_time"
                                      name="pickup_date_time"
                                      className="form-control"
                                      placeholderText="Select pickup date and time"
                                      selected={formik.values.pickup_date_time}
                                      onChange={(date) =>
                                        formik.setFieldValue(
                                          "pickup_date_time",
                                          date,
                                        )
                                      }
                                      onBlur={formik.handleBlur}
                                      minDate={new Date()}
                                      filterDate={(date) =>
                                        [3, 4, 5].includes(date.getDay())
                                      }
                                      showTimeSelect
                                      timeIntervals={30}
                                      filterTime={(time) => {
                                        const d = new Date(time);
                                        const minutes =
                                          d.getHours() * 60 + d.getMinutes();
                                        return (
                                          minutes >= 8 * 60 &&
                                          minutes <= 15 * 60
                                        );
                                      }}
                                      dateFormat="MMMM dd, yyyy h:mm aa"
                                      autoComplete="off"
                                      renderCustomHeader={({
                                        date,
                                        decreaseMonth,
                                        increaseMonth,
                                        prevMonthButtonDisabled,
                                        nextMonthButtonDisabled,
                                      }) => (
                                        <div className="flex items-center justify-between px-2 py-1">
                                          <button
                                            type="button"
                                            onClick={decreaseMonth}
                                            disabled={prevMonthButtonDisabled}
                                            className={`${
                                              prevMonthButtonDisabled &&
                                              "cursor-not-allowed opacity-50"
                                            } inline-flex p-1 text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50`}
                                          >
                                            <ChevronLeftIcon className="w-4 h-4" />
                                          </button>
                                          <span className="font-semibold text-gray-700">
                                            {format(date, "MMMM yyyy")}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={increaseMonth}
                                            disabled={nextMonthButtonDisabled}
                                            className={`${
                                              nextMonthButtonDisabled &&
                                              "cursor-not-allowed opacity-50"
                                            } inline-flex p-1 text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50`}
                                          >
                                            <ChevronRightIcon className="w-4 h-4" />
                                          </button>
                                        </div>
                                      )}
                                    />
                                  </div>
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* ADDRESS */}
                  <div className="border-b border-gray-200 pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Address
                      </h4>
                    </header>
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Block/House No.
                        </label>
                        <InputForm
                          type="text"
                          id="block_no"
                          name="block_no"
                          placeholder="Block/House No."
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Street Name
                        </label>
                        <InputForm
                          type="text"
                          id="street_name"
                          name="street_name"
                          placeholder="Street Name"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Subdivision
                        </label>
                        <InputForm
                          type="text"
                          id="subdivision"
                          name="subdivision"
                          placeholder="Subdivision"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Province <span className="text-red-500">*</span>
                          </label>
                          <select
                            className="form-control"
                            name="province"
                            id="province"
                            onChange={(e) => {
                              formik.setFieldValue("province", e.target.value);
                            }}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              Select Province
                            </option>
                            {ProvinceData?.data?.map((province, i) => {
                              return (
                                <option value={province.prov_code} key={i}>
                                  {province.prov_name}
                                </option>
                              );
                            })}
                          </select>
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.province &&
                              formik.errors.province && (
                                <div>{formik.errors.province}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          City <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="form-control"
                          name="city"
                          id="city"
                          onChange={(e) => {
                            formik.setFieldValue("city", e.target.value);
                          }}
                          value={formik.values.city}
                          onBlur={formik.handleBlur}
                        >
                          <option value="" selected>
                            Select City
                          </option>
                          {!CitiesIsFetching &&
                            CitiesStatus == "success" &&
                            CitiesData?.data?.map((city, i) => {
                              return (
                                <option value={city.city_code} key={i}>
                                  {city.city_name}
                                </option>
                              );
                            })}
                        </select>
                        <div className="help-block text-red-400 text-xs mt-1">
                          {formik.touched.city && formik.errors.city && (
                            <div>{formik.errors.city}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Barangay
                          </label>
                          <select
                            className="form-control"
                            name="barangay"
                            id="barangay"
                            onChange={(e) => {
                              formik.setFieldValue("barangay", e.target.value);
                            }}
                            value={formik.values.barangay}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              Select Barangay
                            </option>
                            {!BarangayIsFetching &&
                              BarangayStatus == "success" &&
                              BarangayData?.data?.map((brgy, i) => {
                                return (
                                  <option value={brgy.id} key={i}>
                                    {brgy.brgy_loc}
                                  </option>
                                );
                              })}
                            {
                              <div className="help-block text-red-400 text-xs mt-1">
                                {formik.touched.barangay &&
                                  formik.errors.barangay && (
                                    <div>{formik.errors.barangay}</div>
                                  )}
                              </div>
                            }
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* OCCUPATION */}
                  <div className="border-b border-gray-200 pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Occupation / School Details
                      </h4>
                    </header>

                    <div className="grid sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Profession / Occupation{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="profession"
                            name="profession"
                            placeholder="Profession/Occupation"
                            className="form-control"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "profession",
                                e.target.value,
                              );
                            }}
                            value={formik.values?.profession}
                            onBlur={formik.handleBlur}
                          />
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.profession &&
                              formik.errors.profession && (
                                <div>{formik.errors.profession}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            School / Office
                          </label>
                          <input
                            type="text"
                            id="school_office"
                            name="school_office"
                            placeholder="School / Office"
                            className="form-control"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "school_office",
                                e.target.value,
                              );
                            }}
                            value={formik.values?.school_office}
                            onBlur={formik.handleBlur}
                          />
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.school_office &&
                              formik.errors.school_office && (
                                <div>{formik.errors.school_office}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="sm:col-span-2 col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            School / Office Address
                          </label>
                          <input
                            type="text"
                            id="school_office_address"
                            name="school_office_address"
                            placeholder="School / Office Address"
                            className="form-control"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "school_office_address",
                                e.target.value,
                              );
                            }}
                            value={formik.values?.school_office_address}
                            onBlur={formik.handleBlur}
                          />
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.school_office_address &&
                              formik.errors.school_office_address && (
                                <div>{formik.errors.school_office_address}</div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-gray-200 pb-5 mb-5">
                    <div className="grid grid-cols-1 gap-x-4">
                      <header className="card-header mb-1">
                        <h4 className="text-blue text-lg font-semibold">
                          How did you know about PMHA?
                        </h4>
                      </header>
                      <div className="col-span-1">
                        <label className="field-label block mb-2">
                          (Check all that apply)
                        </label>
                        <div className="grid">
                          <label className="checkbox-form-group text-base mb-1">
                            <input
                              className="checkbox-form-control"
                              value="Social Media Post (e.g. Facebook, X(Twitter), Instagram, TikTok, Youtube)"
                              name="social_media_post"
                              id="social_media_post"
                              type="checkbox"
                              checked={formik.values.know_about_pmha.includes(
                                "Social Media Post (e.g. Facebook, X(Twitter), Instagram, TikTok, Youtube)",
                              )}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                handleCheckBoxInput(value, checked);
                              }}
                            />
                            Social Media Post (e.g. Facebook, X(Twitter),
                            Instagram, TikTok, Youtube)
                          </label>
                          <label className="checkbox-form-group text-base mb-1">
                            <input
                              className="checkbox-form-control"
                              value="Recommendation/Referral from a Teacher/Professor/Friend"
                              name="recommendation_referral"
                              id="recommendation_referral"
                              type="checkbox"
                              checked={formik.values.know_about_pmha.includes(
                                "Recommendation/Referral from a Teacher/Professor/Friend",
                              )}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                handleCheckBoxInput(value, checked);
                              }}
                            />
                            Recommendation/Referral from a
                            Teacher/Professor/Friend
                          </label>
                          <label className="checkbox-form-group text-base mb-1">
                            <input
                              className="checkbox-form-control"
                              value="Membership Booth Set Up (Events) and other Onsite Activities"
                              name="membership_activities"
                              id="membership_activities"
                              type="checkbox"
                              checked={formik.values.know_about_pmha.includes(
                                "Membership Booth Set Up (Events) and other Onsite Activities",
                              )}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                handleCheckBoxInput(value, checked);
                              }}
                            />
                            Membership Booth Set Up (Events) and other Onsite
                            Activities
                          </label>
                          <label className="checkbox-form-group text-base mb-1">
                            <input
                              className="checkbox-form-control"
                              value="Internship Site/Recommendation"
                              name="internship_recommendation"
                              id="internship_recommendation"
                              type="checkbox"
                              checked={formik.values.know_about_pmha.includes(
                                "Internship Site/Recommendation",
                              )}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                handleCheckBoxInput(value, checked);
                              }}
                            />
                            Internship Site/Recommendation
                          </label>
                          <label className="checkbox-form-group text-base">
                            <input
                              className="checkbox-form-control"
                              value="Word of Mouth"
                              name="word_of_mouth"
                              id="word_of_mouth"
                              type="checkbox"
                              checked={formik.values.know_about_pmha.includes(
                                "Word of Mouth",
                              )}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                handleCheckBoxInput(value, checked);
                              }}
                            />
                            Word of Mouth
                          </label>
                        </div>
                        <div className="help-block text-red-400 mt-2">
                          {formik.touched.know_about_pmha &&
                            formik.errors.know_about_pmha && (
                              <div>{formik.errors.know_about_pmha}</div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* PAYMENT */}
                  <div className="pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Payment
                      </h4>
                    </header>

                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Payment Method{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="payment_method"
                            id="payment_method"
                            className="form-control"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "payment_method",
                                e.target.value,
                              );
                            }}
                            value={formik.values?.payment_method}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              -- Select Payment Method --
                            </option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="GCash">GCash</option>
                            <option value="Cash">Cash</option>
                          </select>
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.payment_method &&
                              formik.errors.payment_method && (
                                <div>{formik.errors.payment_method}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <label className="field-label">Membership Fee</label>
                        <div className="form-group">
                          <div className="flex">
                            <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-gray-600 text-sm">
                              PHP
                            </span>
                            <input
                              type="text"
                              name="contribution"
                              ref={contributionRef}
                              id="contribution"
                              className="form-control rounded-l-none"
                              defaultValue={0}
                              disabled
                            />
                          </div>
                          {/* <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.contribution &&
                              formik.errors.contribution && (
                                <div>{formik.errors.contribution}</div>
                              )}
                          </div> */}
                        </div>
                      </div>
                    </div>
                    {formik.values.payment_method == "Bank Transfer" ? (
                      <>
                        <div className="animate-[wiggle_1s_ease-in-out_infinite] rounded-md p-8 mb-5 bg-[#EEF1F6]">
                          <p className="mb-3">Bank transfer information: </p>
                          <div className="max-w-[180px] pb-3 ml-5">
                            <Image src={MetroBankLogo} alt="Metrobank Logo" />
                          </div>
                          <div className="ml-5">
                            <div className="flex items-center">
                              <p className="basis-[150px]">Bank Name:</p>
                              <p className="font-bold">Metrobank</p>
                            </div>
                            <div className="flex items-center">
                              <p className="basis-[150px]">Account Name:</p>
                              <p className="font-bold">
                                {" "}
                                Philippine Mental Health Association, Inc
                              </p>
                            </div>
                            <div className="flex items-center">
                              <p className="basis-[150px]">Account No.:</p>
                              <p className="font-bold"> 232-3232-50049-3</p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}
                    {formik.values.payment_method == "GCash" ? (
                      <>
                        <div className="animate-[wiggle_1s_ease-in-out_infinite] rounded-md p-8 mb-5 bg-[#EEF1F6]">
                          <div className="flex flex-wrap">
                            <div>
                              <p className="text-lg text-black mb-3">
                                Scan the QR Code:{" "}
                              </p>
                              <div className="max-w-[180px] pb-3">
                                <Image src={GCashLogo} alt="GCash Logo" />
                              </div>
                            </div>
                            <div className="max-w-[200px] ml-8">
                              <Image src={GCashEARD} alt="GCash QR Code" />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}
                    {formik.values.payment_method !== "Cash" && (
                      <div className="flex items-center gap-2 mb-5">
                        <label htmlFor="" className="field-label">
                          Upload Proof of Payment{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div>
                          <Widget
                            className=""
                            publicKey={NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
                            imagesOnly={true}
                            id="proof_of_payment"
                            name="proof_of_payment"
                            tabs="file"
                            onFileSelect={(file) => {
                              console.log("File changed: ", file);

                              if (file) {
                                file.progress((info) =>
                                  console.log("File progress: ", info.progress),
                                );
                                file.done((info) => {
                                  console.log("File uploaded: ", info);
                                  // setCVPic(info.cdnUrl)
                                  formik.setFieldValue(
                                    "proof_of_payment",
                                    info,
                                  );
                                  // saveMediaFile(info)
                                });
                              }
                            }}
                          />
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.proof_of_payment &&
                              formik.errors.proof_of_payment && (
                                <div>{formik.errors.proof_of_payment}</div>
                              )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="">
                      <div className="flex gap-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-2 basis-10"
                          value={true}
                          name="acknowledgement"
                          id="acknowledgement"
                          onChange={(e) => {
                            formik.setFieldValue(
                              "acknowledgement",
                              e.target.checked,
                            );
                          }}
                        />
                        <label
                          className="form-check-label text-gray-800"
                          htmlFor="acknowledgement"
                        >
                          Pursuant to the Data Privacy Act of 2012, I hereby
                          give my consent to PMHA to process my personal data as
                          a member of the Association. I understand that the
                          processing of my personal data shall be limited to the
                          purpose specified.{" "}
                          <span className="text-red-500">*</span>
                        </label>
                      </div>
                      {
                        <div className="help-block text-red-400 text-xs mt-1">
                          {formik.touched.acknowledgement &&
                            formik.errors.acknowledgement && (
                              <div>{formik.errors.acknowledgement}</div>
                            )}
                        </div>
                      }
                    </div>
                  </div>
                  <div className="text-right">
                    <button
                      type="submit"
                      disabled={
                        isLoading || !formik.isValid || !formik.dirty
                      }
                      className="btn btn-primary rounded-full"
                    >
                      <Spinner isLoading={isLoading} />
                      {!isLoading && "Submit"}
                    </button>
                  </div>
                </form>
              ) : (
                //#region Organization
                <form onSubmit={formik.handleSubmit} className="">
                  <div className="border-b border-gray-200 pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Organization Information
                      </h4>
                    </header>
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-3">
                        <div className="form-group">
                          <label htmlFor="name_of_org" className="field-label">
                            Name of School/Agency/Organization/Office{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Name of School/Agency/Organization/Office"
                            name="name_of_org"
                            id="name_of_org"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "name_of_org",
                                e.target.value,
                              );
                            }}
                            onBlur={formik.handleBlur}
                          />
                          <div className="text-red-400 text-xs help-block mt-1">
                            {formik.touched.name_of_org &&
                              formik.errors.name_of_org && (
                                <div>{formik.errors.name_of_org}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <SelectInputForm
                          id="honorifics"
                          label="Salutation"
                          isRequired={true}
                          placeholder="Salutation"
                          formik={formik}
                          options={HONORIFICS}
                        />
                      </div>
                      {formik.values.honorifics === "Others" && (
                        <div className="col-span-1">
                          <label htmlFor="" className="field-label">
                            Other Salutation
                          </label>
                          <InputFormV2
                            id="others_honorifics"
                            isRequired={true}
                            name="others_honorifics"
                            placeholder="Others"
                            formik={formik}
                          />
                        </div>
                      )}
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Name of Point Person{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Name of Point Person"
                          name="point_person"
                          id="point_person"
                          onChange={(e) => {
                            {
                              formik.setFieldValue(
                                "point_person",
                                e.target.value,
                              );
                            }
                          }}
                          onBlur={formik.handleBlur}
                        />
                        <div className="text-red-400 text-xs help-block mt-1">
                          {formik.touched.point_person &&
                            formik.errors.point_person && (
                              <div>{formik.errors.point_person}</div>
                            )}
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Contact No. <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-x-2">
                            <div className="form-control !w-16 leading-7 tracking-wider">
                              +63
                            </div>
                            <input
                              type="number"
                              id="contact_no"
                              name="contact_no"
                              placeholder="9123456789"
                              className="form-control"
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "contact_no",
                                  e.target.value,
                                );
                              }}
                              onBlur={formik.handleBlur}
                            />
                          </div>
                          <div className="text-red-400 text-xs help-block mt-1">
                            {formik.touched.contact_no &&
                              formik.errors.contact_no && (
                                <div>{formik.errors.contact_no}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Email"
                            name="email"
                            id="email"
                            onChange={(e) => {
                              {
                                formik.setFieldValue("email", e.target.value);
                              }
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                          />
                          <div className="text-red-400 text-xs help-block mt-1">
                            {formik.touched.email && formik.errors.email && (
                              <div>{formik.errors.email}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="sm:col-span-2 col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Social Media Account/Website of
                            School/Agency/Organization/Office
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Social Media Account/Website of School/Agency/Organization/Office"
                            name="socials"
                            id="socials"
                            onChange={(e) => {
                              {
                                formik.setFieldValue("socials", e.target.value);
                              }
                            }}
                            onBlur={formik.handleBlur}
                          />
                          <div className="text-red-400 text-xs help-block mt-1">
                            {formik.touched.socials &&
                              formik.errors.socials && (
                                <div>{formik.errors.socials}</div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-gray-200 pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Organization Address
                      </h4>
                    </header>
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Block/House No.
                        </label>
                        <InputForm
                          type="text"
                          id="block_no"
                          name="block_no"
                          placeholder="Block/House No."
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Street Name
                        </label>
                        <InputForm
                          type="text"
                          id="street_name"
                          name="street_name"
                          placeholder="Street Name"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <label htmlFor="" className="field-label">
                          Subdivision
                        </label>
                        <InputForm
                          type="text"
                          id="subdivision"
                          name="subdivision"
                          placeholder="Subdivision"
                          withFormik={true}
                          formik={formik}
                          parentClassName="w-full"
                        />
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Province <span className="text-red-500">*</span>
                          </label>
                          <select
                            className="form-control"
                            name="province"
                            id="province"
                            onChange={(e) => {
                              formik.setFieldValue("province", e.target.value);
                            }}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              Select Province
                            </option>
                            {ProvinceData?.data?.map((province, i) => {
                              return (
                                <option value={province.prov_code} key={i}>
                                  {province.prov_name}
                                </option>
                              );
                            })}
                          </select>
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.province &&
                              formik.errors.province && (
                                <div>{formik.errors.province}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            City <span className="text-red-500">*</span>
                          </label>
                          <select
                            className="form-control"
                            name="city"
                            id="city"
                            onChange={(e) => {
                              formik.setFieldValue("city", e.target.value);
                            }}
                            value={formik.values.city}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              Select City
                            </option>
                            {!CitiesIsFetching &&
                              CitiesStatus == "success" &&
                              CitiesData?.data?.map((city, i) => {
                                return (
                                  <option value={city.city_code} key={i}>
                                    {city.city_name}
                                  </option>
                                );
                              })}
                          </select>
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.city && formik.errors.city && (
                              <div>{formik.errors.city}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label htmlFor="" className="field-label">
                            Barangay
                          </label>
                          <select
                            className="form-control"
                            name="barangay"
                            id="barangay"
                            onChange={(e) => {
                              formik.setFieldValue("barangay", e.target.value);
                            }}
                            value={formik.values.barangay}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              Select Barangay
                            </option>
                            {!BarangayIsFetching &&
                              BarangayStatus == "success" &&
                              BarangayData?.data?.map((brgy, i) => {
                                return (
                                  <option value={brgy.id} key={i}>
                                    {brgy.brgy_loc}
                                  </option>
                                );
                              })}
                            {
                              <div className="help-block text-red-400 text-xs">
                                {formik.touched.barangay &&
                                  formik.errors.barangay && (
                                    <div>{formik.errors.barangay}</div>
                                  )}
                              </div>
                            }
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* PAYMENT */}
                  <div className="pb-5 mb-5">
                    <header className="card-header mb-2">
                      <h4 className="text-blue text-lg font-semibold">
                        Payment
                      </h4>
                    </header>
                    <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-x-4">
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="field-label">Payment Method</label>
                          <select
                            name="payment_method"
                            id="payment_method"
                            className="form-control"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "payment_method",
                                e.target.value,
                              );
                            }}
                            value={formik.values.payment_method}
                            onBlur={formik.handleBlur}
                          >
                            <option value="" selected>
                              Payment Method
                            </option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="GCash">GCash</option>
                            <option value="Cash">Cash</option>
                          </select>
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.payment_method &&
                              formik.errors.payment_method && (
                                <div>{formik.errors.payment_method}</div>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="field-label">Membership Fee</label>
                          <div className="flex">
                            <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-gray-600 text-sm">
                              PHP
                            </span>
                            <input
                              type="text"
                              name="contribution"
                              ref={contributionRef}
                              id="contribution"
                              className="form-control rounded-l-none"
                              defaultValue={0}
                              disabled
                            />
                          </div>
                          <div className="help-block text-red-400 text-xs mt-1">
                            {formik.touched.contribution &&
                              formik.errors.contribution && (
                                <div>{formik.errors.contribution}</div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {formik.values.payment_method == "Bank Transfer" ? (
                      <>
                        <div className="animate-[wiggle_1s_ease-in-out_infinite] rounded-md p-8 mb-5 bg-[#EEF1F6]">
                          <p className="mb-3">Bank transfer information: </p>
                          <div className="max-w-[180px] pb-3 ml-5">
                            <Image src={MetroBankLogo} alt="Metrobank Logo" />
                          </div>
                          <div className="ml-5">
                            <div className="flex items-center">
                              <p className="basis-[150px]">Bank Name:</p>
                              <p className="font-bold">Metrobank</p>
                            </div>
                            <div className="flex items-center">
                              <p className="basis-[150px]">Account Name:</p>
                              <p className="font-bold">
                                {" "}
                                Philippine Mental Health Association, Inc
                              </p>
                            </div>
                            <div className="flex items-center">
                              <p className="basis-[150px]">Account No.:</p>
                              <p className="font-bold"> 232-3232-50049-3</p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}
                    {formik.values.payment_method == "GCash" ? (
                      <>
                        <div className="animate-[wiggle_1s_ease-in-out_infinite] rounded-md p-8 mb-5 bg-[#EEF1F6]">
                          <div className="flex flex-wrap">
                            <div>
                              <p className="text-lg text-black mb-3">
                                Scan the QR Code:{" "}
                              </p>
                              <div className="max-w-[180px] pb-3">
                                <Image src={GCashLogo} alt="GCash Logo" />
                              </div>
                            </div>
                            <div className="max-w-[250px] ml-8">
                              <Image src={GCashEARD} alt="GCash QR Code" />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}
                    {formik.values.payment_method !== "Cash" && (
                      <div className="flex items-center gap-2 mb-5">
                        <label htmlFor="" className="field-label">
                          Upload Proof of Payment{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div>
                          <Widget
                            className=""
                            publicKey={NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
                            imagesOnly={true}
                            id="proof_of_payment"
                            name="proof_of_payment"
                            tabs="file"
                            onFileSelect={(file) => {
                              console.log("File changed: ", file);

                              if (file) {
                                file.progress((info) =>
                                  console.log("File progress: ", info.progress),
                                );
                                file.done((info) => {
                                  console.log("File uploaded: ", info);
                                  // setCVPic(info.cdnUrl)
                                  formik.setFieldValue(
                                    "proof_of_payment",
                                    info,
                                  );
                                  // saveMediaFile(info)
                                });
                              }
                            }}
                          />
                          <div className="help-block text-red-400 text-xs">
                            {formik.touched.proof_of_payment &&
                              formik.errors.proof_of_payment && (
                                <div>{formik.errors.proof_of_payment}</div>
                              )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-5">
                      <div className="flex gap-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-2 basis-10"
                          value={true}
                          name="acknowledgement"
                          id="acknowledgement"
                          onChange={(e) => {
                            formik.setFieldValue(
                              "acknowledgement",
                              e.target.checked,
                            );
                          }}
                        />
                        <label
                          className="form-check-label text-gray-800"
                          htmlFor="acknowledgement"
                        >
                          Pursuant to the Data Privacy Act of 2012, I hereby
                          give my consent to PMHA to process my personal data as
                          a member of the Association. I understand that the
                          processing of my personal data shall be limited to the
                          purpose specified.{" "}
                          <span className="text-red-500">*</span>
                        </label>
                      </div>
                      {
                        <div className="help-block text-red-400 text-xs">
                          {formik.touched.acknowledgement &&
                            formik.errors.acknowledgement && (
                              <div>{formik.errors.acknowledgement}</div>
                            )}
                        </div>
                      }
                    </div>
                  </div>
                  <div className="text-right">
                    <button
                      type="submit"
                      disabled={
                        isLoading || !formik.isValid || !formik.dirty
                      }
                      className="btn btn-primary rounded-full"
                    >
                      <Spinner isLoading={isLoading} />
                      {!isLoading && "Submit"}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* {formik.values.first_time_applicant !== "" && (
              
            )} */}

            {/* <pre>{JSON.stringify(formik.errors,null,1)}</pre> */}
          </div>
        </div>
      </section>

      {/* PARTNERS AND LINKAGES */}
      <section className="max-w-screen-2xl mx-auto py-14 md:px-10 sm:px-8 px-6">
        <h2 className="lg:text-4xl text-3xl text-center mb-10">
          Partnerships and Linkages
        </h2>

        <div className="grid xl:grid-cols-8 lg:grid-cols-6 md:grid-cols-5 sm:grid-cols-4 grid-cols-3 xl:gap-10 lg:gap-8 gap-6 mb-5">
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={AimLogo} alt="AimLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={NULogo} alt="NULogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={USTRedCrossLogo} alt="USTRedCrossLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={XavierULogo} alt="XavierULogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={ChildrenIntLogo} alt="ChildrenIntLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={FreseniusLogo} alt="FreseniusLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={CouncilWelfareLogo} alt="CouncilWelfareLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={CargillLogo} alt="CargillLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={JohnsonLogo} alt="JohnsonLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={ObenGroupLogo} alt="ObenGroupLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={PBBLogo} alt="PBBLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={SunlifeLogo} alt="SunlifeLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={TaguigCityLogo} alt="TaguigCityLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={TelusLogo} alt="TelusLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={ToshibaLogo} alt="ToshibaLogo" />
            </div>
          </div>
          <div className="col-span-1">
            <div className="partner-logo-wrap">
              <Image src={UnilabLogo} alt="UnilabLogo" />
            </div>
          </div>
        </div>
      </section>
      <KNModal
        title={modalTitle}
        iconType={iconType}
        isOpen={isOpenModal}
        noFooter={true}
        bodyClass={"max-w-4xl mt-10"}
        closeModal={() => {
          setIsOpenModal(false);
        }}
        body={
          <MembershipModal
            modalMessage={modalMessage}
            setIsOpenModal={setIsOpenModal}
            iconType={iconType}
          />
        }
      />
      <Footer />
      <KNToaster />
    </main>
  );
};

const MembershipModal = ({ modalMessage, setIsOpenModal, iconType }) => {
  return (
    <div>
      <p>{modalMessage}</p>
      <div className="flex w-full flex-row-reverse gap-2 mt-2 text-white">
        {iconType === "info" ? (
          <button
            className="bg-gray-400 hover:bg-gray-600 w-20 py-2 rounded-md"
            onClick={() => {
              setIsOpenModal(false);
              window.location.reload();
            }}
          >
            Close
          </button>
        ) : (
          <button
            className="bg-red-400 hover:bg-red-600 w-20 py-2 rounded-md"
            onClick={() => {
              setIsOpenModal(false);
            }}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const MembershipView = () => {
  return (
    <BaseTemplate
      title="Membership"
      description="Membership"
      body={<Membership />}
    />
  );
};

export default MembershipView;
